import { useState, FC, useEffect, useMemo } from 'react';
import {
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Paper,
    IconButton,
    Box,
    Divider,
    InputAdornment,
    FormHelperText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { normalizeString } from 'utils/Validations';
import { TimekeeperService } from 'services/TimekeeperService';
import { Dayjs } from 'dayjs';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';
import { PaperComponent } from 'shared/ui/draggable/Draggable';

enum TipoDateEnum {
    FECHA_ESTABLECIMIENTO = 'FECHA_ESTABLECIMIENTO',
    FECHA_ESPECIFICA_PERSONA = 'FECHA_ESPECIFICA_PERSONA',
}

// Schemas de validación Zod
const createEstablishmentDateSchema = (t: any, existingNames: string[]) => {
    return z.object({
        name: z
            .string()
            .transform((val) => val.trim())
            .pipe(
                z
                    .string()
                    .min(2, { message: t('general.zod.string.min_chars', { min: 2 }) })
                    .max(100, { message: t('general.zod.string.max_chars', { max: 100 }) })
                    .refine((val) => /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ\-_]+$/.test(val), { message: t('general.zod.string.invalid_characters') })
                    .refine((val) => !existingNames.some((name) => normalizeString(name) === normalizeString(val)), { message: t('accruals.accrual_dates.dialog.alerts.name_exist') }),
            ),
        date: z.custom<Dayjs | null>((val) => val !== null && val !== undefined, {
            message: t('general.zod.date.required'),
        }),
    });
};

const createPersonDateSchema = (t: any, existingNames: string[]) => {
    return z.object({
        name: z
            .string()
            .transform((val) => val.trim())
            .pipe(
                z
                    .string()
                    .min(2, { message: t('general.zod.string.min_chars', { min: 2 }) })
                    .max(100, { message: t('general.zod.string.max_chars', { max: 100 }) })
                    .refine((val) => /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ\-_]+$/.test(val), { message: t('general.zod.string.invalid_characters') })
                    .refine((val) => !existingNames.some((name) => normalizeString(name) === normalizeString(val)), { message: t('accruals.accrual_dates.dialog.alerts.name_exist') }),
            ),
        datePersonId: z
            .number()
            .nullable()
            .refine((val) => val !== null, {
                message: t('general.zod.string.required'),
            }),
    });
};

interface EstablishmentDate {
    id: string;
    name: string;
    date: Dayjs | null;
}

interface PersonSpecificDate {
    id: string;
    name: string;
    datePersonId: number;
}

interface CreateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any;
    reloading: () => void;
    cleanSelected: () => void;
}

interface AccrualDate {
    name: string;
    date: Date | null;
    type_date: TipoDateEnum;
    date_person_id: number | null;
}

const timekeeperService = new TimekeeperService();

const CreateAccrualDate: FC<CreateProps> = ({ open, setOpen, rows, reloading, cleanSelected }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    // Estado para fechas de establecimiento
    const [establishmentDates, setEstablishmentDates] = useState<EstablishmentDate[]>([]);

    // Estado para fechas específicas de persona
    const [personSpecificDates, setPersonSpecificDates] = useState<PersonSpecificDate[]>([]);
    const [availablePersonDates, setAvailablePersonDates] = useState<any[]>([]);

    const [loading, setLoading] = useState(false);
    const [alerts, setAlerts] = useState(false);

    const [editingEstablishmentId, setEditingEstablishmentId] = useState<string | null>(null);
    const [editingEstablishmentValues, setEditingEstablishmentValues] = useState<{ name: string; date: Dayjs | null }>({ name: '', date: null });
    const [editingPersonId, setEditingPersonId] = useState<string | null>(null);
    const [editingPersonValues, setEditingPersonValues] = useState<{ name: string; datePersonId: number | null }>({ name: '', datePersonId: null });

    // Obtener todos los nombres existentes para validación
    const existingEstablishmentNames = useMemo(() => [...rows.map((row: any) => row.name), ...establishmentDates.map((date) => date.name)], [rows, establishmentDates]);

    const existingPersonNames = useMemo(() => [...rows.map((row: any) => row.name), ...personSpecificDates.map((date) => date.name)], [rows, personSpecificDates]);

    // Schemas con validaciones dinámicas
    const establishmentSchema = useMemo(() => createEstablishmentDateSchema(t, existingEstablishmentNames), [t, existingEstablishmentNames]);

    const personSchema = useMemo(() => createPersonDateSchema(t, existingPersonNames), [t, existingPersonNames]);

    type EstablishmentFormValues = z.infer<typeof establishmentSchema>;
    type PersonFormValues = z.infer<typeof personSchema>;

    // Form para fechas de establecimiento
    const {
        control: establishmentControl,
        register: establishmentRegister,
        handleSubmit: handleEstablishmentSubmit,
        reset: resetEstablishment,
        formState: { errors: establishmentErrors },
    } = useForm<EstablishmentFormValues>({
        resolver: zodResolver(establishmentSchema),
        mode: 'onChange',
        defaultValues: {
            name: '',
            date: undefined,
        },
    });

    const establishmentNameValue = useWatch({ control: establishmentControl, name: 'name' });

    // Form para fechas de persona
    const {
        control: personControl,
        register: personRegister,
        handleSubmit: handlePersonSubmit,
        reset: resetPerson,
        formState: { errors: personErrors },
    } = useForm<PersonFormValues>({
        resolver: zodResolver(personSchema),
        mode: 'onChange',
        defaultValues: {
            name: '',
            datePersonId: null,
        },
    });

    useEffect(() => {
        const data = async () => {
            const response = await timekeeperService.accrual_date.getAll({ type_date__eq: TipoDateEnum.FECHA_ESPECIFICA_PERSONA });
            console.log('data', response);
            setAvailablePersonDates(response.length > 0 ? response : []);
        };

        if (open) {
            data();
        }
    }, [open]);

    const addEstablishmentDate = (data: EstablishmentFormValues) => {
        const newDate: EstablishmentDate = {
            id: Date.now().toString(),
            name: data.name,
            date: data.date,
        };
        setEstablishmentDates([...establishmentDates, newDate]);
        resetEstablishment();
    };

    const removeEstablishmentDate = (id: string) => {
        setEstablishmentDates(establishmentDates.filter((date) => date.id !== id));
    };

    const addPersonSpecificDate = (data: PersonFormValues) => {
        const newDate: PersonSpecificDate = {
            id: Date.now().toString(),
            name: data.name,
            datePersonId: data.datePersonId!,
        };
        setPersonSpecificDates([...personSpecificDates, newDate]);
        resetPerson();
    };

    const removePersonSpecificDate = (id: string) => {
        setPersonSpecificDates(personSpecificDates.filter((date) => date.id !== id));
    };

    const startEditEstablishment = (date: EstablishmentDate) => {
        setEditingEstablishmentId(date.id);
        setEditingEstablishmentValues({ name: date.name, date: date.date });
    };

    const saveEditEstablishment = (id: string) => {
        setEstablishmentDates(establishmentDates.map((d) => (d.id === id ? { ...d, name: editingEstablishmentValues.name, date: editingEstablishmentValues.date } : d)));
        setEditingEstablishmentId(null);
    };

    const startEditPerson = (date: PersonSpecificDate) => {
        setEditingPersonId(date.id);
        setEditingPersonValues({ name: date.name, datePersonId: date.datePersonId });
    };

    const saveEditPerson = (id: string) => {
        setPersonSpecificDates(personSpecificDates.map((d) => (d.id === id ? { ...d, name: editingPersonValues.name, datePersonId: editingPersonValues.datePersonId! } : d)));
        setEditingPersonId(null);
    };

    const disabled = () => {
        return establishmentDates.length === 0 && personSpecificDates.length === 0;
    };

    const closeForm = () => {
        setEstablishmentDates([]);
        setPersonSpecificDates([]);
        resetEstablishment();
        resetPerson();
        setOpen(false);
        setAlerts(false);
        setLoading(false);
        cleanSelected();
    };

    const changes = () => {
        if (establishmentDates.length > 0 || personSpecificDates.length > 0) {
            setAlerts(true);
        } else {
            closeForm();
        }
    };

    const CreateAccrualDate = async () => {
        setLoading(true);

        const datesToCreate: AccrualDate[] = [
            ...establishmentDates.map((date) => ({
                name: date.name,
                date: date.date ? date.date.toDate() : null,
                type_date: TipoDateEnum.FECHA_ESTABLECIMIENTO,
                date_person_id: 0,
            })),
            ...personSpecificDates.map((date) => ({
                name: date.name,
                date: null,
                type_date: TipoDateEnum.FECHA_ESPECIFICA_PERSONA,
                date_person_id: date.datePersonId,
            })),
        ];

        try {
            // Crear todos los registros
            console.log('Dates to create:', datesToCreate);
            const createdDates = await timekeeperService.accrual_date.create(datesToCreate);
            console.log('Created Accrual Dates:', createdDates);

            enqueueSnackbar(t('accruals.accrual_dates.dialog.alerts.add.success'), { variant: 'success' });
            closeForm();
            reloading();
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.accrual_dates.dialog.alerts.add.error'), { variant: 'error' });
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes();
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="md"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.accrual_dates.dialog.titles.add')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>{alerts && <AlertCustom alerts={alerts} setAlerts={setAlerts} closeForm={closeForm} t={t} />}</Box>
            <DialogContent dividers>
                <Grid container spacing={3} sx={{ flexWrap: 'nowrap', alignItems: 'stretch' }}>
                    {/* COLUMNA IZQUIERDA: Formularios */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                            {t('accruals.accrual_dates.dialog.sections.establishment_dates')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label={t('accruals.accrual_dates.dialog.inputs.name')}
                                    autoComplete="off"
                                    variant="outlined"
                                    {...establishmentRegister('name')}
                                    error={!!establishmentErrors.name}
                                    slotProps={{
                                        input: {
                                            endAdornment: <InputAdornment position="end">{`${establishmentNameValue?.length || 0}/100`}</InputAdornment>,
                                        },
                                        htmlInput: { maxLength: 100 },
                                    }}
                                />
                                <FormHelperText sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, px: 1.75 }} component={'span'}>
                                    <Box component={'span'} color={(theme) => theme.palette.error.main}>
                                        {establishmentErrors.name?.message ?? ''}
                                    </Box>
                                </FormHelperText>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 8 }}>
                                <Controller
                                    name="date"
                                    control={establishmentControl}
                                    render={({ field }) => (
                                        <DatePicker
                                            label={t('accruals.accrual_dates.dialog.inputs.date')}
                                            value={field.value}
                                            onChange={field.onChange}
                                            format="DD/MM/YYYY"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: !!establishmentErrors.date,
                                                    helperText: establishmentErrors.date?.message,
                                                    size: 'small',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Button variant="contained" fullWidth onClick={handleEstablishmentSubmit(addEstablishmentDate)}>
                                    {t('general.buttons.add')}
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                            {t('accruals.accrual_dates.dialog.sections.person_specific_dates')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label={t('accruals.accrual_dates.dialog.inputs.name')}
                                    autoComplete="off"
                                    variant="outlined"
                                    {...personRegister('name')}
                                    error={!!personErrors.name}
                                    helperText={personErrors.name?.message}
                                    slotProps={{
                                        htmlInput: { maxLength: 100 },
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 8 }}>
                                <Controller
                                    name="datePersonId"
                                    control={personControl}
                                    render={({ field }) => (
                                        <FormControl fullWidth error={!!personErrors.datePersonId} size="small">
                                            <InputLabel>{t('accruals.accrual_dates.dialog.inputs.date')}</InputLabel>
                                            <Select
                                                {...field}
                                                value={field.value ?? ''}
                                                label={t('accruals.accrual_dates.dialog.inputs.date')}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    field.onChange(Number(value));
                                                }}
                                            >
                                                <MenuItem value={0}>{t('accruals.accrual_dates.dialog.inputs.types_date_selectable.hire_date')}</MenuItem>
                                                {availablePersonDates.map((date) => (
                                                    <MenuItem key={date.accrual_date_id} value={date.accrual_date_id}>
                                                        {date.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {personErrors.datePersonId && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                                    {personErrors.datePersonId.message}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Button variant="contained" fullWidth onClick={handlePersonSubmit(addPersonSpecificDate)}>
                                    {t('general.buttons.add')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Divider orientation="vertical" flexItem />

                    {/* COLUMNA DERECHA: Listas con edición/eliminación */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                            {t('accruals.accrual_dates.dialog.sections.establishment_dates')}
                        </Typography>
                        <Paper variant="outlined" sx={{ mb: 2, minHeight: 106, maxHeight: 106, overflowY: 'auto' }}>
                            {establishmentDates.length === 0 ? (
                                <Box sx={{ p: 2, textAlign: 'center', minHeight: 106, maxHeight: 106, overflowY: 'auto' }}>
                                    <Typography variant="body2" color="textSecondary">
                                        —
                                    </Typography>
                                </Box>
                            ) : (
                                establishmentDates.map((date, index) => (
                                    <Box key={date.id}>
                                        {index > 0 && <Divider />}
                                        {editingEstablishmentId === date.id ? (
                                            <Box sx={{ p: 1.5 }}>
                                                <Grid container spacing={1} alignItems="flex-start">
                                                    <Grid size={{ xs: 12 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label={t('accruals.accrual_dates.dialog.inputs.name')}
                                                            value={editingEstablishmentValues.name}
                                                            onChange={(e) => setEditingEstablishmentValues((prev) => ({ ...prev, name: e.target.value }))}
                                                            slotProps={{ htmlInput: { maxLength: 100 } }}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 8 }}>
                                                        <DatePicker
                                                            label={t('accruals.accrual_dates.dialog.inputs.date')}
                                                            value={editingEstablishmentValues.date}
                                                            onChange={(val) => setEditingEstablishmentValues((prev) => ({ ...prev, date: val }))}
                                                            format="DD/MM/YYYY"
                                                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 4 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
                                                            <IconButton size="small" color="primary" onClick={() => saveEditEstablishment(date.id)}>
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => setEditingEstablishmentId(null)}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        ) : (
                                            <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {date.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {date.date?.format('DD/MM/YYYY')}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                                    <IconButton size="small" onClick={() => startEditEstablishment(date)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => removeEstablishmentDate(date.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                ))
                            )}
                        </Paper>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                            {t('accruals.accrual_dates.dialog.sections.person_specific_dates')}
                        </Typography>
                        <Paper variant="outlined" sx={{ maxHeight: 106, minHeight: 106, overflowY: 'auto' }}>
                            {personSpecificDates.length === 0 ? (
                                <Box sx={{ p: 2, textAlign: 'center', maxHeight: 106, minHeight: 106, overflowY: 'auto' }}>
                                    <Typography variant="body2" color="textSecondary">
                                        —
                                    </Typography>
                                </Box>
                            ) : (
                                personSpecificDates.map((date, index) => (
                                    <Box key={date.id}>
                                        {index > 0 && <Divider />}
                                        {editingPersonId === date.id ? (
                                            <Box sx={{ p: 1.5 }}>
                                                <Grid container spacing={1} alignItems="flex-start">
                                                    <Grid size={{ xs: 12 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label={t('accruals.accrual_dates.dialog.inputs.name')}
                                                            value={editingPersonValues.name}
                                                            onChange={(e) => setEditingPersonValues((prev) => ({ ...prev, name: e.target.value }))}
                                                            slotProps={{ htmlInput: { maxLength: 100 } }}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 8 }}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>{t('accruals.accrual_dates.dialog.inputs.date')}</InputLabel>
                                                            <Select
                                                                value={editingPersonValues.datePersonId ?? ''}
                                                                label={t('accruals.accrual_dates.dialog.inputs.date')}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    setEditingPersonValues((prev) => ({ ...prev, datePersonId: Number(value) }));
                                                                }}
                                                            >
                                                                <MenuItem value={0}>{t('accruals.accrual_dates.dialog.inputs.types_date_selectable.hire_date')}</MenuItem>
                                                                {availablePersonDates.map((d) => (
                                                                    <MenuItem key={d.accrual_date_id} value={d.accrual_date_id}>
                                                                        {d.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid size={{ xs: 4 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
                                                            <IconButton size="small" color="primary" onClick={() => saveEditPerson(date.id)}>
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => setEditingPersonId(null)}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        ) : (
                                            <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {date.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {date.datePersonId === 0
                                                            ? t('accruals.accrual_dates.dialog.inputs.types_date_selectable.hire_date')
                                                            : availablePersonDates.find((d) => d.accrual_date_id === date.datePersonId)?.name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                                    <IconButton size="small" onClick={() => startEditPerson(date)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => removePersonSpecificDate(date.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                ))
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={() => CreateAccrualDate()} disabled={disabled()} loading={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { CreateAccrualDate };
